/**
 * R2 Storage Adapter
 * 
 * Uses Cloudflare R2 via the backend media API for file storage.
 * This is the cloud storage adapter for authenticated users.
 */

import { supabase } from '../../lib/supabase';
import logger from '../../utils/logger';

export interface UploadOptions {
  metadata?: Record<string, any>;
  nodeId?: string;
  flowId?: string;
  folder?: string;
  isPublic?: boolean;
  scope?: 'global' | 'flow'; // 'flow' stores under the flow, 'global' at user level
  skipDeduplication?: boolean; // Skip deduplication check for this upload
}

export interface FileInfo {
  fileId: string;
  id: string;
  url: string;
  name: string;
  type: string;
  size: number;
  category: string;
  key?: string;
}

export interface FileMetadata {
  id: string;
  name: string;
  type: string;
  size: number;
  url: string;
  downloadUrl?: string;
  category?: string;
  uploadedAt?: string;
  metadata?: Record<string, any>;
  key?: string;
}

export interface StorageInfo {
  totalFiles: number;
  totalSize: number;
  usedQuota: number;
  availableQuota: number;
  environment: string;
}

/**
 * Get the API base URL
 */
function getApiBase(): string {
  return typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:3001'
    : '';
}

/**
 * Get current user ID from Supabase auth
 */
async function getUserId(): Promise<string | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    return user?.id || null;
  } catch (error) {
    logger.warn('[R2Adapter] Failed to get user ID:', error);
    return null;
  }
}

/**
 * Determine file category from MIME type
 */
function getCategory(mimeType: string): string {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  if (mimeType === 'application/pdf') return 'document';
  return 'document';
}

export class R2Adapter {
  private environment: string;
  private apiBase: string;
  private fileListCache: Map<string, { data: FileMetadata[], timestamp: number }> = new Map();
  private CACHE_TTL = 5000; // 5 seconds cache

  constructor() {
    this.environment = 'production';
    this.apiBase = getApiBase();
  }

  /**
   * Clear the file list cache (call after upload/delete operations)
   */
  private clearCache() {
    this.fileListCache.clear();
  }

  /**
   * Get cached data if available and not expired
   */
  private getCached(key: string): FileMetadata[] | null {
    const cached = this.fileListCache.get(key);
    if (cached && (Date.now() - cached.timestamp) < this.CACHE_TTL) {
      return cached.data;
    }
    return null;
  }

  /**
   * Set cache data
   */
  private setCache(key: string, data: FileMetadata[]) {
    this.fileListCache.set(key, { data, timestamp: Date.now() });
  }

  /**
   * Check if adapter is ready
   */
  async isReady(): Promise<boolean> {
    const userId = await getUserId();
    return !!userId;
  }

  /**
   * Upload file to R2 via backend API
   */
  async uploadFile(file: File, options: UploadOptions = {}): Promise<FileInfo> {
    const userId = await getUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (options.nodeId) formData.append('nodeId', options.nodeId);
      if (options.flowId) formData.append('flowId', options.flowId);
      if (options.scope) formData.append('scope', options.scope);
      if (options.skipDeduplication) formData.append('skipDeduplication', 'true');

      const response = await fetch(`${this.apiBase}/api/media/upload`, {
        method: 'POST',
        headers: {
          'x-user-id': userId,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Upload failed' }));
        throw new Error(error.error || `Upload failed: ${response.status}`);
      }

      const result = await response.json();
      const category = getCategory(file.type);

      logger.debug(`[R2Adapter] File uploaded: ${file.name} -> ${result.key}`);

      // Use proxy URL to avoid CORS issues
      const proxyUrl = this.getProxyUrl(result.key, userId);

      // Clear cache after upload to force refresh
      this.clearCache();

      return {
        fileId: result.key,
        id: result.key,
        url: proxyUrl, // Use proxy URL instead of signed R2 URL
        name: file.name,
        type: file.type,
        size: file.size,
        category,
        key: result.key,
      };
    } catch (error) {
      logger.error('[R2Adapter] Upload failed:', error);
      throw new Error(`Upload failed: ${(error as Error).message}`);
    }
  }

  /**
   * Upload base64 data to R2
   */
  async uploadBase64(
    data: string,
    fileName: string,
    mimeType: string,
    options: UploadOptions = {}
  ): Promise<FileInfo> {
    const userId = await getUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    try {
      const response = await fetch(`${this.apiBase}/api/media/upload-base64`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
        },
        body: JSON.stringify({
          data,
          fileName,
          mimeType,
          nodeId: options.nodeId,
          flowId: options.flowId,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Upload failed' }));
        throw new Error(error.error || `Upload failed: ${response.status}`);
      }

      const result = await response.json();
      const category = getCategory(mimeType);

      // Calculate size from base64 (approximate)
      const base64Content = data.replace(/^data:[^;]+;base64,/, '');
      const size = Math.round((base64Content.length * 3) / 4);

      logger.debug(`[R2Adapter] Base64 uploaded: ${fileName} -> ${result.key}`);

      // Use proxy URL to avoid CORS issues
      const proxyUrl = this.getProxyUrl(result.key, userId);

      // Clear cache after upload to force refresh
      this.clearCache();

      return {
        fileId: result.key,
        id: result.key,
        url: proxyUrl, // Use proxy URL instead of signed R2 URL
        name: fileName,
        type: mimeType,
        size,
        category,
        key: result.key,
      };
    } catch (error) {
      logger.error('[R2Adapter] Base64 upload failed:', error);
      throw new Error(`Upload failed: ${(error as Error).message}`);
    }
  }

  /**
   * Get file metadata
   */
  async getFile(fileId: string): Promise<FileMetadata> {
    const userId = await getUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    try {
      const params = new URLSearchParams({ key: fileId });
      const response = await fetch(`${this.apiBase}/api/media/metadata?${params}`, {
        headers: {
          'x-user-id': userId,
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(`File not found: ${fileId}`);
        }
        throw new Error(`Failed to get file: ${response.status}`);
      }

      const metadata = await response.json();
      
      // Use proxy URL to avoid CORS issues
      const proxyUrl = this.getProxyUrl(fileId, userId);

      return {
        id: fileId,
        name: fileId.split('/').pop() || fileId,
        type: metadata.contentType,
        size: metadata.size,
        url: proxyUrl, // Use proxy URL instead of signed R2 URL
        category: getCategory(metadata.contentType),
        uploadedAt: metadata.lastModified,
        key: fileId,
      };
    } catch (error) {
      logger.error('[R2Adapter] Get file failed:', error);
      throw new Error(`Failed to get file: ${(error as Error).message}`);
    }
  }

  /**
   * Get file data as File object
   */
  async getFileData(fileId: string): Promise<File> {
    const userId = await getUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    try {
      const params = new URLSearchParams({ key: fileId });
      const response = await fetch(`${this.apiBase}/api/media/download?${params}`, {
        headers: {
          'x-user-id': userId,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to download file: ${response.status}`);
      }

      const blob = await response.blob();
      const fileName = fileId.split('/').pop() || 'file';
      return new File([blob], fileName, { type: blob.type });
    } catch (error) {
      logger.error('[R2Adapter] Get file data failed:', error);
      throw new Error(`Failed to get file data: ${(error as Error).message}`);
    }
  }

  /**
   * Get signed URL for file
   */
  async getSignedUrl(fileId: string, expiresIn: number = 3600): Promise<string> {
    const userId = await getUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    try {
      const params = new URLSearchParams({ 
        key: fileId,
        expiresIn: expiresIn.toString(),
      });
      const response = await fetch(`${this.apiBase}/api/media/url?${params}`, {
        headers: {
          'x-user-id': userId,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to get URL: ${response.status}`);
      }

      const data = await response.json();
      return data.url;
    } catch (error) {
      logger.error('[R2Adapter] Get signed URL failed:', error);
      throw new Error(`Failed to get signed URL: ${(error as Error).message}`);
    }
  }

  /**
   * Delete file from R2
   */
  async deleteFile(fileId: string): Promise<boolean> {
    const userId = await getUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    try {
      const params = new URLSearchParams({ key: fileId });
      const response = await fetch(`${this.apiBase}/api/media/file?${params}`, {
        method: 'DELETE',
        headers: {
          'x-user-id': userId,
        },
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Delete failed' }));
        throw new Error(error.error || `Delete failed: ${response.status}`);
      }

      logger.debug(`[R2Adapter] File deleted: ${fileId}`);
      
      // Clear cache after delete to force refresh
      this.clearCache();
      
      return true;
    } catch (error) {
      logger.error('[R2Adapter] Delete failed:', error);
      return false;
    }
  }

  /**
   * Get a proxy URL for a file (avoids CORS issues)
   * Always returns a relative URL so it works on any environment
   * Includes userId for authentication
   */
  getProxyUrl(fileKey: string, userId?: string): string {
    const params = new URLSearchParams({ key: fileKey });
    if (userId) {
      params.set('userId', userId);
    }
    return `/api/media/proxy?${params}`;
  }

  /**
   * List files for current user (global assets only)
   */
  async listFiles(type?: 'images' | 'videos' | 'audio' | 'documents'): Promise<FileMetadata[]> {
    const userId = await getUserId();
    if (!userId) {
      return [];
    }

    // Check cache first
    const cacheKey = `list-${userId}-${type || 'all'}`;
    const cached = this.getCached(cacheKey);
    if (cached) {
      logger.debug('[R2Adapter] Returning cached file list');
      return cached;
    }

    try {
      const params = new URLSearchParams();
      if (type) params.set('type', type);

      const response = await fetch(`${this.apiBase}/api/media/list?${params}`, {
        headers: {
          'x-user-id': userId,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to list files: ${response.status}`);
      }

      const data = await response.json();
      const files = data.files.map((file: any) => {
        // Infer mime type from the file path/extension
        const fileName = file.key.split('/').pop() || file.key;
        const mimeType = this.getMimeTypeFromKey(file.key);
        
        // Use proxy URL to avoid CORS issues with R2 signed URLs
        const proxyUrl = this.getProxyUrl(file.key, userId);
        
        return {
          id: file.key,
          name: fileName,
          type: mimeType,
          size: file.size,
          url: proxyUrl, // Use proxy URL instead of signed R2 URL
          downloadUrl: file.url, // Keep the signed URL for direct downloads if needed
          uploadedAt: file.lastModified,
          key: file.key,
          category: getCategory(mimeType),
        };
      });

      // Cache the results
      this.setCache(cacheKey, files);
      
      return files;
    } catch (error) {
      logger.error('[R2Adapter] List files failed:', error);
      return [];
    }
  }

  /**
   * List files for a specific flow (flow assets only)
   */
  async listFlowFiles(flowId: string, type?: 'images' | 'videos' | 'audio' | 'documents'): Promise<FileMetadata[]> {
    const userId = await getUserId();
    if (!userId) {
      return [];
    }

    // Check cache first
    const cacheKey = `flow-${userId}-${flowId}-${type || 'all'}`;
    const cached = this.getCached(cacheKey);
    if (cached) {
      logger.debug('[R2Adapter] Returning cached flow file list');
      return cached;
    }

    try {
      const params = new URLSearchParams();
      if (type) params.set('type', type);

      const response = await fetch(`${this.apiBase}/api/media/list/flow/${encodeURIComponent(flowId)}?${params}`, {
        headers: {
          'x-user-id': userId,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to list flow files: ${response.status}`);
      }

      const data = await response.json();
      const files = data.files.map((file: any) => {
        // Infer mime type from the file path/extension
        const fileName = file.key.split('/').pop() || file.key;
        const mimeType = this.getMimeTypeFromKey(file.key);
        
        // Use proxy URL to avoid CORS issues with R2 signed URLs
        const proxyUrl = this.getProxyUrl(file.key, userId);
        
        return {
          id: file.key,
          name: fileName,
          type: mimeType,
          size: file.size,
          url: proxyUrl,
          downloadUrl: file.url,
          uploadedAt: file.lastModified,
          key: file.key,
          category: getCategory(mimeType),
        };
      });

      // Cache the results
      this.setCache(cacheKey, files);
      
      return files;
    } catch (error) {
      logger.error('[R2Adapter] List flow files failed:', error);
      return [];
    }
  }

  /**
   * Infer mime type from file key (path and extension)
   */
  private getMimeTypeFromKey(key: string): string {
    // Check folder path for type hint
    if (key.includes('/images/')) {
      const ext = key.split('.').pop()?.toLowerCase();
      const imageTypes: Record<string, string> = {
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        png: 'image/png',
        gif: 'image/gif',
        webp: 'image/webp',
        svg: 'image/svg+xml',
        bmp: 'image/bmp',
        ico: 'image/x-icon',
      };
      return imageTypes[ext || ''] || 'image/png';
    }
    if (key.includes('/videos/')) {
      const ext = key.split('.').pop()?.toLowerCase();
      const videoTypes: Record<string, string> = {
        mp4: 'video/mp4',
        webm: 'video/webm',
        mov: 'video/quicktime',
        avi: 'video/x-msvideo',
        mkv: 'video/x-matroska',
      };
      return videoTypes[ext || ''] || 'video/mp4';
    }
    if (key.includes('/audio/')) {
      const ext = key.split('.').pop()?.toLowerCase();
      const audioTypes: Record<string, string> = {
        mp3: 'audio/mpeg',
        wav: 'audio/wav',
        ogg: 'audio/ogg',
        m4a: 'audio/mp4',
        flac: 'audio/flac',
      };
      return audioTypes[ext || ''] || 'audio/mpeg';
    }
    if (key.includes('/documents/')) {
      const ext = key.split('.').pop()?.toLowerCase();
      const docTypes: Record<string, string> = {
        pdf: 'application/pdf',
        doc: 'application/msword',
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        txt: 'text/plain',
        json: 'application/json',
        csv: 'text/csv',
      };
      return docTypes[ext || ''] || 'application/octet-stream';
    }
    return 'application/octet-stream';
  }

  /**
   * Get storage usage info
   */
  async getStorageInfo(): Promise<StorageInfo> {
    const userId = await getUserId();
    if (!userId) {
      return {
        totalFiles: 0,
        totalSize: 0,
        usedQuota: 0,
        availableQuota: 0,
        environment: this.environment,
      };
    }

    try {
      const response = await fetch(`${this.apiBase}/api/media/usage`, {
        headers: {
          'x-user-id': userId,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to get storage info: ${response.status}`);
      }

      const data = await response.json();
      return {
        totalFiles: data.fileCount,
        totalSize: data.totalSize,
        usedQuota: data.totalSize,
        availableQuota: 10 * 1024 * 1024 * 1024, // 10GB default quota
        environment: this.environment,
      };
    } catch (error) {
      logger.error('[R2Adapter] Get storage info failed:', error);
      return {
        totalFiles: 0,
        totalSize: 0,
        usedQuota: 0,
        availableQuota: 0,
        environment: this.environment,
      };
    }
  }

  /**
   * Test storage functionality
   */
  async runTests(): Promise<{
    canInitialize: boolean;
    canUpload: boolean;
    canRetrieve: boolean;
    canDelete: boolean;
    environment: string;
    error?: string;
  }> {
    const results = {
      canInitialize: false,
      canUpload: false,
      canRetrieve: false,
      canDelete: false,
      environment: this.environment,
    };

    try {
      results.canInitialize = await this.isReady();
      if (!results.canInitialize) {
        return { ...results, error: 'Not authenticated' };
      }

      // Test upload
      const testContent = 'R2 adapter test file';
      const testFile = new File([testContent], 'r2-test.txt', { type: 'text/plain' });
      const uploadResult = await this.uploadFile(testFile);
      results.canUpload = !!uploadResult.key;

      // Test retrieve
      if (uploadResult.key) {
        const fileData = await this.getFile(uploadResult.key);
        results.canRetrieve = !!fileData.url;

        // Test delete
        results.canDelete = await this.deleteFile(uploadResult.key);
      }

      return results;
    } catch (error) {
      return { ...results, error: (error as Error).message };
    }
  }

  // ============================================================================
  // Context methods (stubs - context is stored in Supabase, not R2)
  // These methods exist for interface compatibility with other adapters
  // ============================================================================

  /**
   * Save file context (stored in Supabase, not R2)
   */
  async saveFileContext(_fileId: string, context: any): Promise<any> {
    // Context is stored in the flows/templates tables in Supabase, not in R2
    // This is a stub for interface compatibility
    logger.debug('[R2Adapter] saveFileContext called (no-op for R2)');
    return context;
  }

  /**
   * Get file context (stored in Supabase, not R2)
   */
  async getFileContext(_fileId: string): Promise<any | null> {
    // Context is stored in Supabase, not R2
    logger.debug('[R2Adapter] getFileContext called (no-op for R2)');
    return null;
  }

  /**
   * List all contexts (stored in Supabase, not R2)
   */
  async listContexts(): Promise<any[]> {
    // Context is stored in Supabase, not R2
    logger.debug('[R2Adapter] listContexts called (no-op for R2)');
    return [];
  }

  /**
   * Clear all data (dangerous - requires confirmation)
   */
  async clearAllData(_confirmationToken: string | null = null): Promise<boolean> {
    // This would delete all user files from R2
    // For safety, we don't implement this - use the dashboard instead
    logger.warn('[R2Adapter] clearAllData called - not implemented for safety');
    return false;
  }
}

export default R2Adapter;
